import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { Controller, useForm } from "react-hook-form";

import {
  COLORS,
  emptyString,
  logTypeList,
  storesConstants,
  activityLogsConstants,
} from "../../constants";
import {
  useSpinner,
  Option,
  Spinner,
  DataTable,
  handleSpace,
  ValueContainer,
  loadingIndicator,
  convertToDateToUnix,
  convertFromDateToUnix,
  datePreferenceUtility,
  paginatedPostRequestQueryBody,
} from "../../../../shared";
import CustomDateFilter from "../../../../shared/components/dateFilter/customDateFilter";
import { activityLogsActions, storeActions } from "../../redux/actions";
import { ACTIVITY_LOGS_COLUMNS } from "./utility";

//Custome styles for react select
const styleReactSelect = {
  control: (styles) => ({
    ...styles,
    borderRadius: "6px",
    minHeight: "2.3rem",
    borderColor: COLORS.ASH_GREY,
    "&:hover": {
      borderColor: COLORS.ASH_GREY,
    },
    boxShadow: "none",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: state.isSelected ? "transparent" : emptyString,
    color: "black",
  }),
};

const defaultFormState = {
  userName: emptyString,
  brandName: [],
  level: [],
};

const { FETCH_ACTIVITY_LOGS, OR, AND } = activityLogsConstants;

const ActivityLogs = (props) => {
  const {
    allBrands,
    activityLogsReducer: { activityLogsData, activityLogsPaginationData },
    getAllBrands,
    getActivityLogs,
  } = props;

  const defaultDate = {
    ...datePreferenceUtility(),
  };

  const { control, register, handleSubmit } = useForm();

  //Initialize states
  const filters = useRef({ userName: [], brandName: [], level: [] });
  const [dateState, setDateState] = useState(defaultDate);
  const [formData, setFormData] = useState(defaultFormState);
  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [filteredDataValue, setFilteredDataValue] = useState(emptyString);

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Create query string for filters
  const generateQuery = (filterObj) => {
    //Create array of filters name from filters object e.g(['userName','brandName','level'])
    const filterNames = Object.keys(filterObj);
    const queryArray = filterNames.reduce((arr, name) => {
      const values = filterObj[name];
      if (values.length) {
        const filterValues = values.map((value) => `${name}="${value}"`);
        arr.push(`(${filterValues.join(OR)})`); // Surround filter values with parentheses
      }
      return arr;
    }, []);

    const { startDate, endDate } = dateState;
    queryArray.push(generateDateExpression(startDate, endDate));

    //If we select multiple filters e.g(state, name and ownerName),
    //then each filter will be separated with AND
    return queryArray.join(AND);
  };

  const generateDateExpression = (startDate, endDate) => {
    return `(createdAt >= ${convertFromDateToUnix(
      startDate
    )}${AND}createdAt <= ${convertToDateToUnix(endDate)})`;
  };

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const requestBody = paginatedPostRequestQueryBody(
      activityLogsPaginationData,
      pageNumber,
      emptyString,
      sortBy || sorting,
      filteredDataValue ||
      generateDateExpression(dateState.startDate, dateState.endDate),
      emptyString
    );
    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    getActivityLogs(requestBody);
  };

  //OnSubmit function to submit all the data on submission
  const onSubmit = () => {
    filters.current.userName = formData.userName.length
      ? [formData.userName.trim()]
      : [];
    filters.current.brandName = formData.brandName.map((b) => b.name);
    filters.current.level = formData.level.map((l) => l.name);
    const query = generateQuery(filters.current);
    setFilteredDataValue(query);
  };

  //Fetch data from these calls before the page loads
  useEffect(() => {
    getAllBrands(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getData(0);
    // eslint-disable-next-line
  }, [filteredDataValue]);

  return (
    <div className="container-fluid py-3">
      <div className="notification-form card shadow px-4 pt-2 mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="form-group mb-2 col-md-6 col-sm-12 d-flex justify-content-between">
                <h6 htmlFor="userName" className="mt-2 col-md-3 col-4">
                  User Name:
                </h6>
                <input
                  type="text"
                  name="userName"
                  value={formData.userName}
                  minLength={1}
                  maxLength={100}
                  placeholder="Enter User Name"
                  {...register("userName", {
                    onChange: (e) =>
                      setFormData({
                        ...formData,
                        userName: e.target.value,
                      }),
                  })}
                  onKeyDown={(e) => handleSpace(e)}
                  className="form-control user-fields font-14-px text-secondary"
                />
              </div>
              <div className="form-group mb-2 col-md-6 col-sm-12 d-flex justify-content-between align-self-start">
                <h6 htmlFor="brandId" className="mt-2 col-md-3 col-4">
                  Brand Name:
                </h6>
                <Controller
                  name="brandId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={formData.brandName}
                      closeMenuOnSelect={false}
                      options={[...allBrands]}
                      components={{
                        Option,
                        ValueContainer: ValueContainer,
                        IndicatorSeparator: () => null,
                        LoadingIndicator: loadingIndicator,
                      }}
                      isMulti
                      hideSelectedOptions={false}
                      maxMenuHeight={200}
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => option.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          brandName: e,
                        });
                      }}
                      placeholder="Brand Name"
                      isSearchable={true}
                      styles={styleReactSelect}
                      className="w70 font-14-px text-secondary"
                      id="brandId"
                      isLoading={spinner(storesConstants.FETCH_ALL_BRANDS)}
                      isDisabled={
                        spinner(storesConstants.FETCH_ALL_BRANDS)
                      }
                    />
                  )}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group mb-2 col-md-6 col-sm-12 d-flex justify-content-between align-self-start">
                <h6 htmlFor="levelId" className="mt-2 col-md-3 col-4">
                  Log Type:
                </h6>
                <Controller
                  name="levelId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={formData.level}
                      closeMenuOnSelect={false}
                      options={[...logTypeList]}
                      components={{
                        Option,
                        ValueContainer: ValueContainer,
                        IndicatorSeparator: () => null,
                        LoadingIndicator: loadingIndicator,
                      }}
                      isMulti
                      hideSelectedOptions={false}
                      maxMenuHeight={200}
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => option.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          level: e,
                        });
                      }}
                      placeholder="Log Type"
                      isSearchable={true}
                      styles={styleReactSelect}
                      className="w70 font-14-px text-secondary"
                      id="levelId"
                    />
                  )}
                />
              </div>
              <div className="form-group mb-2 col-md-6 col-sm-12 d-sm-none"></div>
            </div>
            <div className="row">
              <div className="form-group col-md-6 col-sm-12 d-flex justify-content-between align-self-start">
                <h6 htmlFor="name" className="mt-2 col-md-3 col-4">
                  Date Range:
                </h6>
                <div className="pt-2 w70 google-social-link">
                  <CustomDateFilter
                    dateState={dateState}
                    setDateState={setDateState}
                  />
                </div>
              </div>
            </div>
            <div className="form-group d-flex justify-content-between justify-content-sm-end">
              <button type="submit" className="btn user-permission-button px-4">
                Go
              </button>
              <button
                type="button"
                className="btn user-permission-button px-4 mx-1 me-0"
                onClick={() => {
                  setDateState(defaultDate);
                  setFormData(defaultFormState);
                  setFilteredDataValue(
                    generateDateExpression(
                      defaultDate.startDate,
                      defaultDate.endDate
                    )
                  );
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="notification-form card mt-3 shadow">
        <div className="card-body p-0">
          <div className="row">
            <div className="col-12 py-3">
              <h5 className="ps-4 m-0">Activity Logs</h5>
            </div>
          </div>
          <hr className="mt-0" />
          {spinner(FETCH_ACTIVITY_LOGS) && <Spinner />}
          <div>
            <DataTable
              columns={ACTIVITY_LOGS_COLUMNS}
              data={activityLogsData}
              totalRows={activityLogsPaginationData.totalElements}
              numberOfRowsPerPage={activityLogsPaginationData.size}
              pageNumber={activityLogsPaginationData.number + 1}
              onPageChange={(pageNum) => getData(pageNum)}
              onColumnSort={(sortBy) =>
                getData(activityLogsPaginationData.number, sortBy)
              }
              sortedBy={sorting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  allBrands: state.storesReducer.allBrands,
  activityLogsReducer: state.activityLogsReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getActivityLogs: (body) =>
    dispatch(activityLogsActions.getActivityLogs(body)),
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityLogs);
